import {getOperationName} from 'apollo-utilities';
import {useMutation} from 'react-apollo';

import {
  MoveRunToGroupMutation,
  MoveRunToGroupMutationVariables,
  RunSelectorRunsDocument,
  RunsStateDeltaQueryDocument,
} from '../../../generated/graphql';
import {useAsyncBatcher} from '../../../util/hooks/useAsyncBatcher';
import {Run} from '../../../util/runTypes';
import {MOVE_RUN_TO_GROUP} from './MoveRunToGroupMutation.query';

// Rather than use a magic string for "RunSelectorRuns" get the query name from
// the document itself. This establishes a hard dependency on the
// RunsSelectorRunsDocument, which should raise some flags if that code gets
// a major refactor someday.
const runSelectorRunsQueryName = getOperationName(RunSelectorRunsDocument)!;
// Same for RSDQ:
const runStateDeltaQueryName = getOperationName(RunsStateDeltaQueryDocument)!;

type UseRunMoverHook = ReturnType<typeof useAsyncBatcher<Run, unknown>>;

type UseRunMoverOpts = {
  groupName: string | null;
  entityName: string;
  projectName: string;
};

/**
 * Moves one or more runs between groups in the run selector. This uses
 * useAsyncBatcher under the hood to batch up HTTP requests. On the final HTTP
 * request, this hook invalidates the queries needed to trigger the run selector
 * to rerender.
 *
 * @param groupName
 * @param entityName
 * @param projectName
 */
export function useRunMover({
  groupName,
  entityName,
  projectName,
}: UseRunMoverOpts): UseRunMoverHook {
  const [moveRunMutation] = useMutation<
    MoveRunToGroupMutation,
    MoveRunToGroupMutationVariables
  >(MOVE_RUN_TO_GROUP);

  function moveRun(run: Run, index: number, runs: Run[]) {
    if (!groupName) {
      throw new RangeError(`Called bulk move operation but groupName is null`);
    }

    // On last run of the mutation, invalidate queries needed to update the UI:
    const refetch =
      index === runs.length - 1
        ? {
            awaitRefetchQueries: true,
            refetchQueries: [runSelectorRunsQueryName, runStateDeltaQueryName],
          }
        : {};

    return moveRunMutation({
      ...refetch,
      variables: {
        groupName,
        entityName,
        projectName,
        runId: run.id,
      },
    });
  }

  return useAsyncBatcher<Run, unknown>(moveRun, {
    showNavigationAwayWarning: true,
  });
}
